import React, { FocusEvent, useCallback, useState } from 'react'
import { useIntl } from 'react-intl'
import { View } from 'react-native'
import { connect, ConnectedProps } from 'react-redux'
import { useLocation, useNavigate } from 'react-router'

import classNames from 'classnames'
import { isEmpty, sortBy } from 'lodash-es'
import { bindActionCreators } from 'redux'

import {
  assignmentHasCompletedResponse,
  getEffectiveRecipientLyraId,
} from '@lyrahealth-inc/shared-app-logic/src/features/assignments/utils'
import { getProgramDisplayLabel } from '@lyrahealth-inc/shared-app-logic/src/features/programConfigs/programHelpers'
import {
  ActivityGroupType,
  Assignment,
  Content,
} from '@lyrahealth-inc/shared-app-logic/src/models/assignments/Assignments'
import { Episode } from '@lyrahealth-inc/shared-app-logic/src/models/episodes/Episodes'
import { AssignmentTagType } from '@lyrahealth-inc/ui-core/lib/lyraTherapy/assignmentsList/AssignmentListItem'
import AssignmentsList from '@lyrahealth-inc/ui-core/lib/lyraTherapy/assignmentsList/AssignmentsList'
import { TypeAheadFieldAdapter } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/formElements/typeAhead/TypeAheadFieldAdapter'
import { useFetcher } from '@lyrahealth-inc/ui-core-crossplatform/src/hooks/useFetcher'

import styles from './assignmentLibrary.module.scss'
import ContentFilter from './ContentFilter'
import { BCC_COACH_SUPERVISOR_ROLES, LT_COACH_ROLES, ROLES } from '../../common/constants/appConstants'
import { CLIENT_ACTIVITIES, CLIENTS_NEW_ASSIGNMENT } from '../../common/constants/routingConstants'
import { hasRole } from '../../common/utils/utils'
import { getLyraTherapyContentsData } from '../../data/lyraTherapy/contentSelectors'
import { AppDispatch, RootState } from '../../data/store'
import { getProviderDetailsData } from '../../providers/data/providerSelectors'
import { useAssignmentsData } from '../assignments/data/hooks/useAssignmentsData'
import { getContentCategoriesTagsByProgramId } from '../data/ltContentsAutoActions'
import { useEpisodesForSelectedClient } from '../episodes/data/hooks/useEpisodesForSelectedClient'
import { useSelectedEpisodeForClient } from '../episodes/data/hooks/useSelectedEpisodeForClient'
import MiniBackNav from '../miniBackNav/MiniBackNav'

const mapContentTypetoDisplayNames = {
  exercise: { displayName: 'Exercise', tabName: 'Exercises' },
  lesson: { displayName: 'Lesson', tabName: 'Lessons' },
  infosheet: { displayName: 'Guide', tabName: 'Guides' },
}

type ContentLibraryProps = ConnectedProps<typeof connector>
/* list out the exercises, lessons, and infosheets for the providers to select from and add to the assignments/curriculum */
export const ContentLibrary: React.FC<ContentLibraryProps> = ({
  contents,
  actions: { getContentCategoriesTagsByProgramId },
  providerDetailsData,
}) => {
  const intl = useIntl()
  const { data: episodes } = useEpisodesForSelectedClient()
  const navigate = useNavigate()
  const location = useLocation()
  const { data: selectedEpisode } = useSelectedEpisodeForClient() as { data: Episode | undefined }
  const { data: assignments } = useAssignmentsData()

  const sessionCount = location?.state?.sessionCount ?? undefined
  const recipientLyraId = location?.state?.recipientLyraId ?? undefined
  const recipientType = location?.state?.recipientType ?? undefined
  const [activeTab, setActiveTab] = useState('Exercises')

  const assignmentMatchesRecipient = useCallback(
    (assignment: Assignment) => {
      if (!recipientLyraId) {
        return true
      }

      return (
        getEffectiveRecipientLyraId(assignment.recipient_id, assignment.patient_id || selectedEpisode?.patient_id) ===
        recipientLyraId
      )
    },
    [recipientLyraId, selectedEpisode?.patient_id],
  )

  const handleContentClicked = (content: Content) => {
    navigate(CLIENTS_NEW_ASSIGNMENT.route, {
      state: {
        content,
        sessionCount,
        ...(recipientLyraId && { recipientLyraId }),
        ...(recipientType && { recipientType }),
      },
    })
  }
  const handleBackClicked = () => {
    const route = CLIENT_ACTIVITIES.route
    navigate(route, { state: { sessionCount } })
  }

  const exercises = sortBy(
    contents.filter((content) => content.group === 'exercise'),
    'title',
  )
  const lessons = sortBy(
    contents.filter((content) => content.group === 'lesson'),
    'title',
  )
  const infosheets = sortBy(
    contents.filter((content) => content.group === 'infosheet'),
    'title',
  )

  const allActivities = exercises.concat(lessons, infosheets)
  const [filteredActivities, setFilteredActivities] = useState(allActivities)

  const [, categoryTags = [], fetchedCategoryTags] = useFetcher(
    [[getContentCategoriesTagsByProgramId, { programId: selectedEpisode?.program_id }]],
    [selectedEpisode?.program_id],
  )
  const sortFilteredActivitiesAs = (type: ActivityGroupType): Content[] => {
    return sortBy(
      filteredActivities.filter((content) => content.group === type),
      'title',
    )
  }
  const handleFilteredActivities = (input: Content[] | FocusEvent<Element> | undefined) => {
    if (isEmpty(input) || !Array.isArray(input)) {
      setFilteredActivities(allActivities)
    } else {
      setFilteredActivities(input)
      setActiveTab(mapContentTypetoDisplayNames[input?.[0].group as keyof typeof mapContentTypetoDisplayNames].tabName)
    }
  }

  const assignmentSessionTags: Record<string, { type: AssignmentTagType; text: string }[]> = {}
  const currentEpisodeAssignments = assignments?.filter(
    (assignment) => assignment.episode_id === selectedEpisode?.id && assignmentMatchesRecipient(assignment),
  )
  contents.forEach((content) => {
    const lastAssignedCurrentEpisode = sortBy(currentEpisodeAssignments, 'session_period')
      .reverse()
      .find((assignment) => assignment.content.id === content.id)
    const otherEpisodesAssignments = assignments?.filter(
      (assignment) =>
        assignment.content.id === content.id &&
        assignment.episode_id !== selectedEpisode?.id &&
        assignmentMatchesRecipient(assignment),
    )
    const otherAssignedEpisodes = episodes
      ?.filter((episode) => otherEpisodesAssignments?.map((assignment) => assignment.episode_id).includes(episode.id))
      .sort((a, b) => (a.start_date > b.start_date ? -1 : 1))
    const lastAssignedOtherEpisodes =
      // In rare occurances(bugs) of episode permission issues, sometimes not all episodes are available to the FE, see https://lyrahealth.atlassian.net/browse/LCDELIVERY-4988
      !isEmpty(otherAssignedEpisodes)
        ? otherEpisodesAssignments?.find((assignment) => assignment.episode_id === otherAssignedEpisodes?.[0].id)
        : undefined

    if (lastAssignedCurrentEpisode) {
      assignmentSessionTags[content.id!] = [
        { type: AssignmentTagType.default, text: `Last assigned session ${lastAssignedCurrentEpisode.session_period}` },
      ]
      if (assignmentHasCompletedResponse(lastAssignedCurrentEpisode)) {
        assignmentSessionTags[content.id!].push({ type: AssignmentTagType.success, text: 'Client completed' })
      }
    } else if (otherAssignedEpisodes?.length) {
      assignmentSessionTags[content.id!] = [
        {
          type: AssignmentTagType.default,
          text: `Assigned in previous ${
            otherAssignedEpisodes?.[0]?.program_name &&
            otherAssignedEpisodes?.[0]?.program_name !== selectedEpisode?.program_name
              ? `${getProgramDisplayLabel(intl, otherAssignedEpisodes[0].program_name)} `
              : ''
          }episode`,
        },
      ]
      if (assignmentHasCompletedResponse(lastAssignedOtherEpisodes)) {
        assignmentSessionTags[content.id!].push({ type: AssignmentTagType.success, text: 'Client completed' })
      }
    }
  })
  const handleKeyDown = (event: { key: string }, tabToActivate: string) => {
    if (event.key === 'Enter' || event.key === 'Space') {
      setActiveTab(tabToActivate)
    }
  }
  // For now setting the lint rule to disable as moving h3 to interactive element will need
  // additional work and design input
  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
  const renderFilter = () => {
    return (
      <div className={styles['filter-container']}>
        <h3
          onClick={() => setActiveTab('Exercises')}
          onKeyDown={(event) => handleKeyDown(event, 'Exercises')}
          className={classNames({ [styles.active]: activeTab === 'Exercises' })}
          data-test-id='ContentLibrary-exercisesFilter'
        >
          Exercises
        </h3>
        <h3
          onClick={() => setActiveTab('Lessons')}
          onKeyDown={(event) => handleKeyDown(event, 'Lessons')}
          className={classNames({ [styles.active]: activeTab === 'Lessons' })}
          data-test-id='ContentLibrary-lessonsFilter'
        >
          Lessons
        </h3>
        <h3
          onClick={() => setActiveTab('Guides')}
          onKeyDown={(event) => handleKeyDown(event, 'Guides')}
          className={classNames({ [styles.active]: activeTab === 'Guides' })}
          data-test-id='ContentLibrary-guidesFilter'
        >
          Guides
        </h3>
      </div>
    )
  }

  /* eslint-enable jsx-a11y/no-noninteractive-element-interactions */
  return (
    <View>
      <div className={styles.container}>
        <MiniBackNav backFunc={handleBackClicked} subText={`Activities for Session ${sessionCount}`} />

        <div style={{ width: '100%' }}>
          <h2 className={styles['main-header']}>Share new content</h2>
          <div>
            <div>
              <TypeAheadFieldAdapter
                placeholder='Search for content...'
                emptyLabel='No activities found.'
                multiSelect={false}
                options={allActivities}
                labelKey={(content: Content) =>
                  `${content.title} (${
                    mapContentTypetoDisplayNames[content.group as keyof typeof mapContentTypetoDisplayNames].displayName
                  }) ${isEmpty(content.tags) ? '' : `(${content.tags})`}`
                }
                input={{
                  onChange: handleFilteredActivities,
                  onBlur: handleFilteredActivities,
                  name: 'ContentLibrary-typeahead',
                }}
              />
            </div>
            {hasRole(providerDetailsData?.roles, [
              ROLES.LT_THERAPIST,
              ROLES.LT_FELLOW_THERAPIST,
              ...LT_COACH_ROLES,
              ...BCC_COACH_SUPERVISOR_ROLES,
              ROLES.LT_TEENS_THERAPIST,
            ]) &&
              fetchedCategoryTags &&
              categoryTags &&
              Array.isArray(categoryTags) &&
              categoryTags.length > 0 && (
                <ContentFilter
                  allActivities={allActivities}
                  setFilteredActivities={setFilteredActivities}
                  categoryTags={categoryTags}
                  isProviderCoach={hasRole(providerDetailsData?.roles, [
                    ...LT_COACH_ROLES,
                    ...BCC_COACH_SUPERVISOR_ROLES,
                  ])}
                />
              )}
            <div data-test-id='ContentLibrary-filter'>{renderFilter()}</div>
            {activeTab === 'Exercises' && (
              <AssignmentsList
                // @ts-expect-error TS(2769): No overload matches this call.
                assignments={sortFilteredActivitiesAs('exercise')}
                itemClickHandler={handleContentClicked}
                noDataText='No Exercises available'
                assignmentTags={assignmentSessionTags}
              />
            )}
            {activeTab === 'Lessons' && (
              <AssignmentsList
                // @ts-expect-error TS(2769): No overload matches this call.
                assignments={sortFilteredActivitiesAs('lesson')}
                itemClickHandler={handleContentClicked}
                noDataText='No Lessons available'
                assignmentTags={assignmentSessionTags}
              />
            )}
            {activeTab === 'Guides' && (
              <AssignmentsList
                // @ts-expect-error TS(2769): No overload matches this call.
                assignments={sortFilteredActivitiesAs('infosheet')}
                itemClickHandler={handleContentClicked}
                noDataText='No Guides available'
                assignmentTags={assignmentSessionTags}
              />
            )}
          </div>
        </div>
      </div>
    </View>
  )
}

const mapStateToProps = (state: RootState) => {
  return {
    contents: getLyraTherapyContentsData(state),
    providerDetailsData: getProviderDetailsData(state),
  }
}
const mapDispatchToProps = (dispatch: AppDispatch) => ({
  actions: bindActionCreators(
    {
      getContentCategoriesTagsByProgramId,
    },
    dispatch,
  ),
})

const connector = connect(mapStateToProps, mapDispatchToProps)
export default connector(ContentLibrary)
